// SEARCH — the rail's Search seat (2026-09-25). The kit's Spotlight over what
// this app actually holds: its pages, its sources (the installed plugins), and
// your graph — the same EP-scoped /api/nodes the wall's Nucleus reads, so a
// result here can never be something the grant does not reach.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Spotlight, SpotlightInput, SpotlightClear, SpotlightResults,
  SpotlightSection, SpotlightItem, SpotlightEmpty,
} from '@ofself/shell';
import { loadLibrary } from '../lib/identityWall.js';

const PAGES = [
  { id: 'absorber', label: 'Absorber', hint: 'drop a file into your graph' },
  { id: 'plugins', label: 'Plugins', hint: 'sources and tools' },
  { id: 'automations', label: 'Automations', hint: 'what runs, and when' },
];

const match = (q, ...fields) => fields.some((f) => String(f || '').toLowerCase().includes(q));

// /api/nodes is Paradigm's list, proxied: read the rows wherever they ride
const rowsOf = (d) => (Array.isArray(d) ? d : d?.items || d?.nodes || d?.data || d?.results || []);

export default function SearchSpotlight({ open, onClose, onOpenNode }) {
  const [q, setQ] = useState('');
  const [plugins, setPlugins] = useState([]);
  const [nodes, setNodes] = useState(null); // null: not asked; [] asked, none
  const seq = useRef(0);

  useEffect(() => {
    if (!open) { setQ(''); setNodes(null); return; }
    fetch('/api/plugins', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : { plugins: [] }))
      .then((d) => setPlugins(d.plugins || []))
      .catch(() => setPlugins([]));
  }, [open]);

  // the graph is asked after a pause in typing, and only the latest answer lands
  const term = q.trim().toLowerCase();
  useEffect(() => {
    if (!open || term.length < 2) { setNodes(null); return undefined; }
    const mine = ++seq.current;
    const t = setTimeout(() => {
      loadLibrary({ search: term, limit: 12 })
        .then((d) => { if (mine === seq.current) setNodes(rowsOf(d)); })
        .catch(() => { if (mine === seq.current) setNodes([]); });
    }, 220);
    return () => clearTimeout(t);
  }, [open, term]);

  const pages = useMemo(() => (term ? PAGES.filter((p) => match(term, p.label, p.hint)) : PAGES), [term]);
  const sources = useMemo(
    () => (term ? plugins.filter((p) => match(term, p.name, p.description, p.id)) : []).slice(0, 8),
    [term, plugins],
  );

  const go = (view) => { onClose(); window.navigateTo?.('/' + view); };
  const nothing = term && !pages.length && !sources.length && nodes && !nodes.length;

  return (
    <Spotlight open={open} onClose={onClose} label="Search Integrations">
      <SpotlightInput
        placeholder="Search pages, sources and your graph"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          if (pages[0]) go(pages[0].id);
          else if (sources[0]) go('plugins');
        }}
        after={q ? <SpotlightClear onClick={() => setQ('')} /> : null}
      />
      <SpotlightResults>
        {pages.length ? (
          <SpotlightSection title="Pages">
            {pages.map((p) => (
              <SpotlightItem key={p.id} as="button" type="button" onClick={() => go(p.id)}>
                {p.label}<span className="ig-search-hint">{p.hint}</span>
              </SpotlightItem>
            ))}
          </SpotlightSection>
        ) : null}
        {sources.length ? (
          <SpotlightSection title="Sources">
            {sources.map((p) => (
              <SpotlightItem key={p.id} as="button" type="button" onClick={() => go('plugins')}>
                {p.name}<span className="ig-search-hint">{p.description || p.id}</span>
              </SpotlightItem>
            ))}
          </SpotlightSection>
        ) : null}
        {nodes && nodes.length ? (
          <SpotlightSection title="In your graph">
            {nodes.map((n, i) => (
              <SpotlightItem key={n.id || i} as="button" type="button"
                             onClick={() => { onClose(); onOpenNode?.(n); }}>
                {n.title || n.name || n.id}
                <span className="ig-search-hint">{n.schema_name || n.node_type || n.schema || ''}</span>
              </SpotlightItem>
            ))}
          </SpotlightSection>
        ) : null}
        {nothing ? <SpotlightEmpty>Nothing here matches “{q.trim()}”.</SpotlightEmpty> : null}
      </SpotlightResults>
    </Spotlight>
  );
}
